import { NgClass } from "@angular/common";
import { Component, effect, inject, input, OnDestroy } from "@angular/core";
import { catchError, combineLatest, Observable, of, Subject, take, takeUntil } from "rxjs";
import { EstateAgentMarketShare, StatisticsLine } from "src/models/estate/IResidenceSaleInArea";
import { StatisticPanes } from "src/models/map/statistic-panes.enum";
import { AreaBuilderDataService } from "src/services/area-builder-data.service";
import { DemographyDataService } from "src/services/estate/demography-data.service";
import { MarketSalesStateService } from "src/services/estate/market-sales-state.service";
import { MapMode, MapStateService } from "src/services/map/map-state.service";
import { BuyerDemographyComponent } from "./demography/buyer-demography/buyer-demography.component";
import { InhabitantDemographyComponent } from "./demography/inhabitant-demography/inhabitant-demography.component";
import { EstateAgentMarketSharesComponent } from "./estate-agent-market-shares/estate-agent-market-shares.component";
import { PriceStatisticsComponent } from "./price-statistics/price-statistics.component";
import { ReportWizardComponent, wizardOptions } from "./price-statistics/report-wizard/report-wizard.component";
import { ReportsService } from "src/services/reports.service";
import { InternalNavigationService } from "src/services/internal-navigation.service";
import { SubscriptionReportComponent } from "./subscription-report/subscription-report.component";
import { RightSidebarOptions, RightSidebarService } from "src/services/right-sidebar.service";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";

@Component({
    selector: "estate-statistics",
    imports: [
        PriceStatisticsComponent,
        NgClass,
        EstateAgentMarketSharesComponent, InhabitantDemographyComponent, BuyerDemographyComponent,
        InhabitantDemographyComponent,
        BuyerDemographyComponent,
        ReportWizardComponent,
        SubscriptionReportComponent
    ],
    templateUrl: "./estate-statistics.component.html",
    styleUrl: "./estate-statistics.component.scss",
})
export class EstateStatisticsComponent implements OnDestroy {
    statistics = input.required<StatisticsLine[]>();
    estateAgentMarketShares = input.required<EstateAgentMarketShare[]>();
    mapStateService = inject(MapStateService);
    demographyDataService = inject(DemographyDataService);
    areaBuilderService = inject(AreaBuilderDataService);
    reportsService = inject(ReportsService);
    navigationService = inject(InternalNavigationService);
    private readonly authorizationService = inject(EvAuthorizationService);
    private readonly rightSidebarService = inject(RightSidebarService);
    private readonly marketSalesStateService = inject(MarketSalesStateService);

    filterFormAPI = this.marketSalesStateService.filterFormAPI;

    mapMode = MapMode;
    currentPane: StatisticPanes = StatisticPanes.none;
    StatisticPanes = StatisticPanes;
    private canShowReportWizardButtonDueFilterOptions: boolean = false;
    canShowReportWizardButton = false;
    hasMarketSharesRole = this.authorizationService.hasPermissionSignal(EVServiceType.EvWeb_AgentReport, true, false);
    hasDemographicsRole = this.authorizationService.hasPermissionSignal(EVServiceType.EvWeb_Demography, true, false);
    hasSubscriptionReportRole = this.authorizationService.hasPermissionSignal(EVServiceType.EvWeb_SubscriptionReport, true, false);
    canShowReportWizardButtonUseStyling = false;
    showPriceStatisticsReportWizard = false;
    loadingDataForPriceStatisticsReport = false;

    private destroyed$ = new Subject<void>();
 
    ngOnDestroy(): void {
        this.destroyed$.next();
    }

    onCloseStatistics(): void {
        this.rightSidebarService.toggleSidebarVisibility(RightSidebarOptions.Closed);
    }
    
    openReportWizardDialog() {
        this.showPriceStatisticsReportWizard = true;
    }

    closeStatisticsReport(): void {
        this.showPriceStatisticsReportWizard = false;
    }

    generatePriceStatisticsReportV2(opt: { options: wizardOptions; generatePdf: boolean }): void {
        const newWindow = opt.generatePdf ? this.navigationService.getLoadRedirectWindow() : null;

        combineLatest([
            this.mapStateService.mapUpdateAdjustedBoundsOrPolygons$,
            this.mapStateService.greenBeltSortOrder$,
        ])
            .pipe(take(1))
            .subscribe(([mapState, sortOrder]) => {
                const filters = {
                    sortOrder,
                    ...this.filterFormAPI(),
                };
                const data = {
                    ...mapState.state,
                    filters: filters,
                    pursuantTransitionYears: this.demographyDataService.pursuantTransitionYears(),
                    includePriceStatistics: opt.options.prices,
                    includeInhabitantDemographics: opt.options.demographyResidents,
                    includeBuyerDemographics: opt.options.demographyBuyers,
                    includeEstateAgentMarketShares: opt.options.estateAgentMarketShares,
                    includeAreaComparison: opt.options.areaComparison,
                    includeMatrixes: opt.options.matrixes,
                };

                this.reportsService
                    .getPriceStatisticReportUrl(data)
                    .pipe(
                        takeUntil(this.destroyed$),
                        catchError((_) => {
                          window.open("/Home/Error", "_blank");
                            // newWindow.location.href = "/Home/Error";
                            return of(null);
                        }),
                    )
                    .subscribe((report) => {
                        if (!report) return;
                        const binaryString = window.atob(report);
                        const bytes = new Uint8Array(binaryString.length);
                        const uInt8Array = bytes.map((_, i) => binaryString.charCodeAt(i));
                        this.createAndShowBlobFile(uInt8Array, newWindow, "application/pdf");
                        this.closeStatisticsReport();
                    });
            });
    }

     private createAndShowBlobFile(body: Uint8Array<ArrayBuffer>, newWindow: Window, mimeType: string = "") {
        const blob = new Blob([body], { type: mimeType });
        let url: string = "";
        if (window.webkitURL != null) {
            url = window.webkitURL.createObjectURL(blob);
        } else {
            url = URL.createObjectURL(blob);
        }
        newWindow.location.href = url;
    }



    showStatisticsPane(pane: StatisticPanes): void {
        switch (pane) {
            case StatisticPanes.price:
                break;
            case StatisticPanes.marketShares:
                break;
            case StatisticPanes.demographyBuyers:
                // this.demographyDataService.setInhabitantVisible(false);
                break;
            case StatisticPanes.demographyInhabitants:
                // this.demographyDataService.setInhabitantVisible(true);
                break;
            case StatisticPanes.areaSelection:
                break;
            case StatisticPanes.subscriptionReport:
                break;
            default:
        }
        this.currentPane = pane;
        this.mapStateService.currentPane.set(pane);
    }

        private setCanShowReportWizardButton(): void {
        this.canShowReportWizardButton = false;
        this.canShowReportWizardButtonUseStyling = false;
        if (this.mapStateService.mapModeSignal() === MapMode.Market) {
            this.canShowReportWizardButton = this.canShowReportWizardButtonDueFilterOptions;
            if (this.canShowReportWizardButton) {
                this.canShowReportWizardButtonUseStyling = true;
            }
        }
    }
}
